import { useEffect, useRef, useState } from "react";
import perflexionLogo from "./assets/perflexion-logo.png";

const API_BASE = process.env.REACT_APP_PERFLEXION_API_URL || "https://localhost:7211";

// ─── Types ────────────────────────────────────────────────────────────────────












// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v, decimals = 4) => {
    if (v == null || isNaN(v)) return "—";
    const s = v.toFixed(decimals);
    // VB6's Format() suppresses the negative sign when a tiny negative value rounds
    // to exactly zero at the given precision — JS's toFixed() does not, and shows
    // "-0.00000" instead of "0.00000". Match VB6's behavior here.
    return parseFloat(s) === 0 ? s.replace(/^-/, "") : s;
};
// VB6: Format(x, "######.###") — ALL three fractional positions are optional "#"
// characters, not forced "0"s. Trailing zero digits (and the decimal point itself,
// if nothing is left) are dropped entirely, rather than always showing a fixed
// decimal count. Used for fields whose mask has no forced trailing zero.
const fmtTrim = (v, maxDecimals) => {
    if (v == null || isNaN(v)) return "—";
    let s = v.toFixed(maxDecimals);
    if (parseFloat(s) === 0) s = s.replace(/^-/, "");
    if (s.includes(".")) {
        s = s.replace(/0+$/, "").replace(/\.$/, "");
    }
    return s;
};

const fmtIn = (v) => fmt(v, 4);
// VB6: WireDia(I) = Format(varWireDia(I) * varUM(1), "#.#####0") — a longer mask than
// WireSpace/Shrinkage/LayerDia/MinDia/MaxDia, all of which use "0.###0" (4 decimals).
const fmtWireDia = (v) => fmt(v, 5);

// VB6: UMBritish_Click / UMMetric_Click (lines 18122-18158). Index meanings match
// the ShaftCalculationService.cs um[] array exactly.
const UM_LABELS = {
    1: ["In", "mm"],
    2: ["Lb/Ft", "Kg./m"],
    3: ["Deg/Lb-In/Ft", "Deg/N-m/m"],
    4: ["Lb-In\u00b2", "N-m\u00b2\u00d710\u207b\u00b3"],
    5: ["Lb.In.", "N-m"],
    6: ["PSI/Lb-In", "MPa/N-m"],
    7: ["PSI/1/In", "MPa/m"],
    8: ["In/Ft/Lb-In", "mm/m/N-m"],
    9: ["Lbs.", "Kg."],
    10: ["Ft", "m"],
    11: ["Degree/Ft", "Degree/m"],
    12: ["Feet", "Meter"],
};

const umLbl = (index, isMetric) => UM_LABELS[index][isMetric ? 1 : 0];

// VB6: "Select Case varUTSTOL * varUM(5) ... varFrmtStr3" (ShowResults, lines 19462-19481).
// The Select Case is evaluated ONCE against TOL's magnitude, then that SAME format
// string is applied to both TOL and LOL — LOL's own magnitude is not consulted.
const utsDecimals = (tolValue) => {
    const abs = Math.abs(tolValue);
    if (abs < 10) return 2;
    if (abs <= 99.9) return 1;
    return 0;
};

// VB6: "Select Case varDefLOL * varUM(3) ... varFrmtStr1" (ShowResults, lines 19449-19453).
// Evaluated against LOL's magnitude, then applied to BOTH TOL and LOL (same paired-field
// quirk as utsDecimals above, just keyed off LOL here instead of TOL).
const deflectionDecimals = (lolValue) => {
    const abs = Math.abs(lolValue);
    if (abs < 0.1) return 5;
    if (abs <= 1) return 3;
    if (abs <= 99) return 1;
    return 0;
};

// VB6: "Select Case varCummTorsionStiffTOL * varUM(4) ... varFrmtStr2" (ShowResults,
// lines 19456-19459). Evaluated against TOL's magnitude, applied to both TOL and LOL.
const torsionalStiffnessDecimals = (tolValue) => {
    const abs = Math.abs(tolValue);
    if (abs < 10) return 2;
    if (abs <= 99.9) return 1;
    return 0;
};

// VB6: "If TotalStiffness! > 999 Then B$ = "########" Else B$ = "###.##"" (ShowResults,
// line 19591). TotalStiffness! here is a leftover module-level value from the LAST
// iteration of the Overall/Pair/Combination stiffness loop (Combination/L.O.L., the
// 6th and final of the six combinations computed in TORSIONALSTIFFNESS) — NOT the
// actually-displayed Corrected Stiffness TOL/LOL value. This is the same "select-case
// on one value, apply the result to an unrelated field" pattern as utsDecimals/
// deflectionDecimals above, just reaching further back to a different quantity
// entirely. Reproduced as-is rather than "fixed" to key off the displayed value.
// Takes calc.rawCombinationStiffnessLOL (never unit-converted) — TotalStiffness! is
// always the raw British value in VB6, so using the metric-converted display field
// here would apply the wrong >999 threshold in Metric mode.
const correctedStiffnessDecimals = (combinationLOLRaw) =>
    (Math.abs(combinationLOLRaw) > 999 ? 0 : 2);

// VB6: "If TotalStiff4ASC(I) > 999 Then B2 = "########," Else B2 = "###.#"" then
// "txtAxStiff(I).Caption = Format(TotalStiff4ASC(I) * varUM(4), B2)" (ShowResults,
// lines 19615-19616) — the Axial Stretch tab's "Stiffness" row (each of the six
// Overall/Pair/Combination x T.O.L./L.O.L. cells, checked individually). The trailing
// comma in "########," is a VB6 Format() quirk: it means "divide the displayed number
// by 1000", not just "no decimals" — easy to miss, and originally missed here. Below
// 999 there's no comma, so no /1000 scaling, just 1 decimal place. The >999 check
// itself uses the RAW (never unit-converted) value, same reasoning as
// correctedStiffnessDecimals above.
const axStiffCellDisplay = (raw, converted) =>
    (Math.abs(raw) > 999 ? fmt(converted / 1000, 0) : fmt(converted, 1));

// Renders a T.O.L/L.O.L pair. If there's no L.O.L. value, VB6 showed a single
// merged gray box spanning both columns instead of two separate cells.
const TolLolCells = ({ tol, lol }: { tol: string; lol: string }) => {
    if (!lol) {
        return <td className="pf-val pf-val-merged" colSpan={2}>{tol}</td>;
    }
    return (
        <>
            <td className="pf-val">{tol}</td>
            <td className="pf-val">{lol}</td>
        </>
    );
};

// Produces the 14 [label, tol, lol] rows for one calc result. Used once per populated
// column (Original + any open What-Ifs) in CompareResultsPanel below. Unchanged from
// the original inline version - only pulled out so it can run once per column.
const compareRowValues = (calc) => [
    [`Basic Torsional Deflection (${umLbl(3, calc.isMetric)})`, fmt(calc.deflectionTOL, deflectionDecimals(calc.deflectionLOL)), fmt(calc.deflectionLOL, deflectionDecimals(calc.deflectionLOL))],
    [`Torsional Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.torsionalStiffnessTOL, torsionalStiffnessDecimals(calc.torsionalStiffnessTOL)), fmt(calc.torsionalStiffnessLOL, torsionalStiffnessDecimals(calc.torsionalStiffnessTOL))],
    [`Corrected Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.correctedStiffnessTOL, correctedStiffnessDecimals(calc.rawCombinationStiffnessLOL)), fmt(calc.correctedStiffnessLOL, correctedStiffnessDecimals(calc.rawCombinationStiffnessLOL))],
    [`Axial Stretch (${umLbl(8, calc.isMetric)})`, fmt(calc.axialStretchTOL, 5), fmt(calc.axialStretchLOL, 5)],
    [`Ultimate Torsional Strength (${umLbl(5, calc.isMetric)})`, fmt(calc.utsTOL, utsDecimals(calc.utsTOL)), fmt(calc.utsLOL, utsDecimals(calc.utsTOL))],
    [`Tensile Stress/Unit Torque (${umLbl(6, calc.isMetric)})`, fmt(calc.tensileStressTOL, 0), fmt(calc.tensileStressLOL, 0)],
    [`Bending Stress/Unit Curvature (${umLbl(7, calc.isMetric)})`, fmt(calc.bendingStressTOL, 0), fmt(calc.bendingStressLOL, 0)],
    [`Diameter (${umLbl(1, calc.isMetric)})`, fmtIn(calc.outerDiameterMax), fmtIn(calc.outerDiameterMin)],
    [`Minimum Operating Radius (${umLbl(1, calc.isMetric)})`, fmt(calc.mor, 1), ""],
    [`Non-Damaging Radius (${umLbl(1, calc.isMetric)})`, fmt(calc.ndr, 1), ""],
    [`Overall Bending Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.bendingStiffness, 2), ""],
    ["Total Cost Per 100 Ft", `$${fmt(calc.totalCostPer100Ft, 2)}`, ""],
    ["Total Labor Min. Per 100 Ft", fmt(calc.laborMinAllPer100Ft, 1), ""],
    ["Total Machine Hrs. per 1000 Ft", fmt(calc.machineTimePer1000Ft, 1), ""],
];

// Shared Compare Results table, driven by every currently-open editor's latest calc
// result (0 = Original, 1-4 = What-If), keyed the same way VB6 keys myResults columns
// by varResultNo. A resultNo with no calc yet (What-If not opened, or still loading)
// renders as blank placeholder cells, same as the original always-empty What-If columns.
// Row labels/units follow the Original's unit mode; each column formats its OWN values
// using its own isMetric flag, since decimal-precision decisions are magnitude-driven.
const CompareResultsPanel = ({ results }) => {
    const original = results[0];
    if (!original) return null;
    const labelRows = compareRowValues(original);
    const columnRows = {};
    for (let n = 0; n <= 4; n++) {
        const c = results[n];
        columnRows[n] = c ? compareRowValues(c) : null;
    }

    return (
        <div className="pf-card">
            <div className="pf-subheader">
                <div className="pf-subheader-left">
                    <span className="pf-dot" />
                    <strong>Compare Results</strong>
                </div>
            </div>
            <div className="table-responsive">
                <table className="pf-table pf-compare-table">
                    <thead>
                        <tr>
                            <th className="pf-compare-label-th">Characteristics</th>
                            <th className="pf-compare-th" colSpan={2}>From Original</th>
                            {[1, 2, 3, 4].map((n) => (
                                <th
                                    key={n}
                                    className={`pf-compare-th ${columnRows[n] ? "" : "pf-compare-th-muted"}`}
                                    colSpan={2}
                                >
                                    What If {n}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th className="pf-compare-sub-th"></th>
                            {Array(10).fill(null).map((_, i) => (
                                <th key={i} className="pf-compare-sub-th">{i % 2 === 0 ? "T.O.L." : "L.O.L."}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {labelRows.map(([label], rowIdx) => (
                            <tr key={label}>
                                <td>{label}</td>
                                {[0, 1, 2, 3, 4].map((n) => {
                                    const colData = columnRows[n];
                                    if (!colData) {
                                        return (
                                            <td key={n} className="pf-whatif-empty" colSpan={2}></td>
                                        );
                                    }
                                    const [, tol, lol] = colData[rowIdx];
                                    return <TolLolCells key={n} tol={tol} lol={lol} />;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ─── Icon ─────────────────────────────────────────────────────────────────────

const WoundLayersIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" opacity="0.9" />
        <circle cx="12" cy="12" r="6.7" stroke="white" strokeWidth="1.5" opacity="0.9" />
        <circle cx="12" cy="12" r="3.4" stroke="white" strokeWidth="1.5" opacity="0.9" />
        <circle cx="12" cy="12" r="1.2" fill="white" />
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────


function ShaftEditor({ shaftName, resultNo, onCalcChange, onClose }) {
    const [detail, setDetail] = useState(null);
    const [calc, setCalc] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [wireGrades, setWireGrades] = useState([]);

    // VB6 Form_Load queries WireData once when the window opens (ShaftDesign.frm line
    // 17426-17437) to populate every layer's WireGrade dropdown - same idea here.
    useEffect(() => {
        fetch(`${API_BASE}/api/shafts/wiregrades`)
            .then((r) => r.json())
            .then(setWireGrades)
            .catch(console.error);
    }, []);
    const [units, setUnits] = useState("british");
    const [customRadius, setCustomRadius] = useState("");
    const [customTorque, setCustomTorque] = useState("");
    const [windingOverrides, setWindingOverrides] = useState({});
    const [esmOverride, setEsmOverride] = useState({});
    const [esmLayerOverrides, setEsmLayerOverrides] = useState({});
    const [motorTeethResetKey, setMotorTeethResetKey] = useState(0);
    const [spoolingOverride, setSpoolingOverride] = useState({});
    // VB6: WhatIfCal (WireDia/WireGrade/NoOfWires) + Shrinkage_Change/WireSpace_Change
    // (DiaLoss/WireSpace) + UseCarter_Click (UseCarter, with its own cascade logic).
    // Only offered as editable on What-If windows (resultNo !== 0) per explicit request
    // - VB6 itself doesn't distinguish, but the Original view stays read-only here.
    const [constructionOverrides, setConstructionOverrides] = useState({});
    // VB6: WhatIfCal only reads WireDia/WireGrade/NoOfWires when "Make Changes Then
    // Click Here" (ReCalculate) is clicked - unlike WireSpace/DiaLoss/UseCarter, which
    // apply immediately (see applyImmediateConstructionOverride/applyUseCarterOverride below).
    // Edits to these three fields are staged here until that button is clicked.
    const [pendingConstructionEdits, setPendingConstructionEdits] = useState({});
    const overrideRequestId = useRef(0);
    const [selectMachine, setSelectMachine] = useState("ESM");

    const tabs = [
        "Construction & Basics",
        "Design Specifications",
        "Axial Stretch",
        "Time Estimates & Cost",
        "ESM Settings",
        "Spooling Requirement",
    ];

    // Report every calc update up to the shell, which owns the shared Compare Results
    // table (VB6: ShowResults writing into myResults.txt...(varResultNo) for whichever
    // window/resultNo this editor instance represents).
    useEffect(() => {
        onCalcChange?.(resultNo, calc);
    }, [calc, resultNo, onCalcChange]);

    // Load shaft detail + calc when selected OR when units toggle changes.
    // A genuine shaft change resets everything and does a plain fetch. A units-only
    // toggle preserves whatever overrides are currently active and resends them via
    // the same override endpoint, just with the new isMetric flag.
    const previousShaftName = useRef(null);

    useEffect(() => {
        if (!shaftName) return;
        setLoading(true);

        const isMetric = units === "metric";
        const isNewShaft = previousShaftName.current !== shaftName;
        previousShaftName.current = shaftName;

        if (isNewShaft) {
            setDetail(null);
            setCalc(null);

            // Same out-of-order guard as sendOverrides below: this effect can fire more
            // than once in quick succession (fast shaft switching, a What-If window
            // opening while another request is still in flight, etc.), and fetches can
            // resolve out of order. Discard this response if a newer request has since
            // been issued.
            const requestId = ++overrideRequestId.current;

            Promise.all([
                fetch(`${API_BASE}/api/shafts/byname/${encodeURIComponent(shaftName)}`).then((r) => r.json()),
                fetch(
                    `${API_BASE}/api/shafts/byname/${encodeURIComponent(shaftName)}/calculate?isMetric=${isMetric}`
                ).then((r) => r.json()),
            ])
                .then(([d, c]) => {
                    if (requestId !== overrideRequestId.current) return;
                    setDetail(d);
                    setCalc(c);
                    setLoading(false);
                })
                .catch((e) => {
                    if (requestId !== overrideRequestId.current) return;
                    console.error(e);
                    setLoading(false);
                });
        } else {
            // Units-only toggle: preserve active overrides, just recompute with the new unit.
            sendOverrides(windingOverrides, esmOverride, esmLayerOverrides, spoolingOverride, isMetric)
                .finally(() => setLoading(false));
        }
    }, [shaftName, units]);

    // Reset all per-shaft UI state only when the SHAFT itself changes (not on units toggle)
    useEffect(() => {
        if (!shaftName) return;
        setActiveTab(0);
        setCustomRadius("");
        setCustomTorque("");
        setWindingOverrides({});
        setConstructionOverrides({});
        setPendingConstructionEdits({});
        setEsmOverride({});
        setEsmLayerOverrides({});
        setSelectMachine("ESM");
        setSpoolingOverride({});
    }, [shaftName]);

    // Shared sender — always posts the CURRENT combined winding + ESM overrides, so
    // editing one doesn't clear whatever's already been overridden on the other tab.
    async function sendOverrides(
        windingOv = {},
        esmOv = {},
        esmLayerOv = {},
        spoolingOv = {},
        isMetricOv = false,
        constructionOv = {}
    ) {
        if (!shaftName) return;

        const windingArray = Object.entries(windingOv).map(([ln, v]) => ({
            layerNo: Number(ln),
            spoolType: v.spoolType ?? null,
            machineRPM: v.machineRPM ?? null,
            spoolWeight: v.spoolWeight ?? null,
        }));

        const layerOverridesArray = Object.entries(esmLayerOv).map(([ln, teeth]) => ({
            layerNo: Number(ln),
            motorTeeth: teeth,
        }));

        const constructionArray = Object.entries(constructionOv).map(([ln, v]) => ({
            layerNo: Number(ln),
            wireDia: v.wireDia ?? null,
            wireGrade: v.wireGrade ?? null,
            noOfWires: v.noOfWires ?? null,
            wireSpace: v.wireSpace ?? null,
            diaLoss: v.diaLoss ?? null,
            useCarter: v.useCarter ?? null,
        }));

        const body = {
            windingOverrides: windingArray,
            esmOverride: {
                linearSpeed: esmOv.linearSpeed ?? null,
                startLayer: esmOv.startLayer ?? null,
                endLayer: esmOv.endLayer ?? null,
                firstHead: esmOv.firstHead ?? null,
                isFourHead: esmOv.isFourHead ?? null,
                layerOverrides: layerOverridesArray,
            },
            spoolingOverride: {
                footageToBeWound: spoolingOv.footageToBeWound ?? null,
                spoolerType: spoolingOv.spoolerType ?? null,
            },
            layerOverrides: constructionArray,
        };

        // Guard against out-of-order responses: if several overrides fire in quick
        // succession (e.g. editing Start Layer, End Layer, Head No., then Motor Teeth),
        // requests can resolve out of order. Only the response to the MOST RECENTLY
        // sent request is applied — anything older is discarded.
        const requestId = ++overrideRequestId.current;

        try {
            const res = await fetch(
                `${API_BASE}/api/shafts/byname/${encodeURIComponent(shaftName)}/calculate?isMetric=${isMetricOv}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );
            const data = await res.json();
            if (requestId === overrideRequestId.current) {
                setCalc(data);
            }
        } catch (e) {
            console.error(e);
        }
    }

    // VB6: SpoolType_Change/MachineRPM_Change/SpoolWeight_Change — a live, in-place
    // recalculation using the typed value, layered on top of the auto-computed
    // defaults for every other layer/field. Not saved to the shaft's stored data.
    function applyWindingOverride(
        layerNo,
        field,
        value
    ) {
        const updated = {
            ...windingOverrides,
            [layerNo]: { ...windingOverrides[layerNo], [field]: value },
        };
        setWindingOverrides(updated);
        sendOverrides(updated, esmOverride);
    }

    // VB6: Shrinkage_Change/WireSpace_Change — updates immediately on edit, unlike
    // WireDia/WireGrade/NoOfWires below.
    function applyImmediateConstructionOverride(
        layerNo,
        field,
        value
    ) {
        const updated = {
            ...constructionOverrides,
            [layerNo]: { ...constructionOverrides[layerNo], [field]: value },
        };
        setConstructionOverrides(updated);
        sendOverrides(windingOverrides, esmOverride, esmLayerOverrides, spoolingOverride, units === "metric", updated);
    }

    // VB6: WhatIfCal reads WireDia/WireGrade/NoOfWires only when "Make Changes Then
    // Click Here" is clicked - staged locally here, not sent yet.
    function stagePendingEdit(
        layerNo,
        field,
        value
    ) {
        setPendingConstructionEdits((prev) => ({
            ...prev,
            [layerNo]: { ...prev[layerNo], [field]: value },
        }));
    }

    // The "Make Changes Then Click Here" button itself: merges every staged
    // WireDia/WireGrade/NoOfWires edit into constructionOverrides and sends the whole
    // batch at once, matching VB6's WhatIfCal reading all 14 layers' textboxes on click.
    function applyPendingConstructionEdits() {
        const updated = { ...constructionOverrides };
        for (const [ln, edit] of Object.entries(pendingConstructionEdits)) {
            updated[Number(ln)] = { ...updated[Number(ln)], ...edit };
        }
        setConstructionOverrides(updated);
        setPendingConstructionEdits({});
        sendOverrides(windingOverrides, esmOverride, esmLayerOverrides, spoolingOverride, units === "metric", updated);
    }

    // VB6: UseCarter_Click — checking a layer's box cascades Carter status to every
    // layer AFTER it; unchecking cascades non-Carter to every layer BEFORE it
    // (including layer 0). The whole cascaded set is computed here and sent together,
    // matching VB6's own For-loop over the affected range. UseCarter_Click ALSO
    // hardcodes DiaLoss to 0.002 (checked) or 0.0008 (unchecked) for the same cascaded
    // layers - confirmed by real VB6 test data (checking Use Carter visibly set Dia
    // Loss to exactly 0.002 with no separate manual edit) - not just the Carter flag
    // alone. These are raw-British literals, sent here in the current display unit so
    // they round-trip correctly through the backend's unit-conversion-back logic.
    function applyUseCarterOverride(layerNo: number, checked: boolean) {
        if (!detail) return;
        const lastLayer = calc?.totalLayers ?? detail.totalLayer ?? 0;
        const isMetricNow = units === "metric";
        const diaLossDefault = (checked ? 0.002 : 0.0008) * (isMetricNow ? 25.4 : 1);
        const updated = { ...constructionOverrides };
        if (checked) {
            for (let i = layerNo; i <= lastLayer; i++) {
                updated[i] = { ...updated[i], useCarter: true, diaLoss: diaLossDefault };
            }
        } else {
            for (let i = 0; i <= layerNo; i++) {
                updated[i] = { ...updated[i], useCarter: false, diaLoss: diaLossDefault };
            }
        }
        setConstructionOverrides(updated);
        sendOverrides(windingOverrides, esmOverride, esmLayerOverrides, spoolingOverride, units === "metric", updated);
    }

    // VB6: LinearSpeed_Change/StartingLayerNo_Change/FinalLayerNo_Change/FirstHead_Change/
    // FourHead_Click/FiveHead_Click — same live, in-place recalculation pattern.
    function applyEsmOverride(
        field,
        value
    ) {
        const updated = { ...esmOverride, [field]: value };
        setEsmOverride(updated);
        sendOverrides(windingOverrides, updated);
    }

    // VB6: MotorTeeth_LostFocus — a per-layer override that bypasses the Motor Teeth
    // lookup table entirely, using the typed value directly for that layer's recalc.
    function applyEsmLayerOverride(layerNo: number, motorTeeth: number) {
        const updated = { ...esmLayerOverrides, [layerNo]: motorTeeth };
        setEsmLayerOverrides(updated);
        sendOverrides(windingOverrides, esmOverride, updated);
    }

    // VB6: FootageToBeWound live edit / Conventional-ESM-MSM Spooler radio group.
    function applySpoolingOverride(
        field,
        value
    ) {
        const updated = { ...spoolingOverride, [field]: value };
        setSpoolingOverride(updated);
        sendOverrides(windingOverrides, esmOverride, esmLayerOverrides, updated);
    }

    const lastLayer = detail?.layers.find(
        (l) => l.layerNo === detail.totalLayer
    );

    // Live custom TTR/Deflection rows (VB6: txtRadius(4)_Change / txtTorque(5)_Change) —
    // same already-verified formulas, applied to user-typed values. VB6 converts the
    // typed value back to British first (divide by the relevant um factor) when Metric
    // is selected, runs the raw-British formula, then reapplies the display factor —
    // NOT the same factor used for BendingStiffness/DeflectionTOL/LOL's own display,
    // which is why this uses the RAW values from the API, not calc.bendingStiffness etc.
    const UM1_IN_MM = 25.4;
    const UM5_TORQUE = 8.851;
    const UM11_DEFLECTION = 3.28;
    const isMetricNow = calc?.isMetric ?? false;

    const customRadiusNum = parseFloat(customRadius);
    const hasCustomRadius = customRadiusNum > 0;
    const customRadiusBritish = isMetricNow ? customRadiusNum / UM1_IN_MM : customRadiusNum;
    const customTtrLbIn = hasCustomRadius && calc
        ? ((0.2 * calc.rawBendingStiffness * 3.3) / customRadiusBritish) * (isMetricNow ? UM5_TORQUE : 1)
        : null;
    const customTtrOzIn = hasCustomRadius && calc
        ? ((0.2 * calc.rawBendingStiffness * 3.3) / customRadiusBritish) * 16
        : null;

    const customTorqueNum = parseFloat(customTorque);
    const hasCustomTorque = customTorqueNum > 0;
    const customTorqueBritish = isMetricNow ? customTorqueNum / UM5_TORQUE : customTorqueNum;
    const customDefTOL = hasCustomTorque && calc
        ? ((calc.rawDeflectionTOL * 1.2 * 0.2) + (calc.rawDeflectionTOL * 1.2 * customTorqueBritish)) *
        (isMetricNow ? UM11_DEFLECTION : 1)
        : null;
    const customDefLOL = hasCustomTorque && calc
        ? ((calc.rawDeflectionLOL * 1.2 * 0.2) + (calc.rawDeflectionLOL * 1.2 * customTorqueBritish)) *
        (isMetricNow ? UM11_DEFLECTION : 1)
        : null;

    return (
        <div className="perflexion-app perflexion-editor">
            {loading && (
                <div className="pf-card pf-placeholder">Loading shaft data…</div>
            )}

            {!loading && detail && calc && (
                <>
                    {/* ══ Construction Panel ══ */}
                    <div className="pf-card mb-3">
                        {/* Sub-header bar */}
                        <div className="pf-subheader">
                            <div className="pf-subheader-left">
                                <span className="pf-dot" />
                                <strong>{detail.shaftName} — Original</strong>
                            </div>

                            <div className="pf-subheader-mid">
                                <label className="pf-radio">
                                    <input
                                        type="radio"
                                        checked={units === "british"}
                                        onChange={() => setUnits("british")}
                                    />{" "}
                                    British
                                </label>
                                <label className="pf-radio">
                                    <input
                                        type="radio"
                                        checked={units === "metric"}
                                        onChange={() => setUnits("metric")}
                                    />{" "}
                                    Metric
                                </label>
                                {resultNo !== 0 && (
                                    <button
                                        type="button"
                                        className="pf-recalc-btn"
                                        onClick={applyPendingConstructionEdits}
                                        disabled={Object.keys(pendingConstructionEdits).length === 0}
                                    >
                                        Make Changes Then Click Here
                                    </button>
                                )}
                            </div>

                            <div className="pf-subheader-right">
                                <span className="pf-modulus">
                                    Shear Modulus (Last Layer):{" "}
                                    <strong>{lastLayer?.shearModulus?.toLocaleString() ?? "—"}</strong>
                                </span>
                                <span className="pf-modulus">
                                    Young's Modulus (Last Layer):{" "}
                                    <strong>{lastLayer?.youngModulus?.toLocaleString() ?? "—"}</strong>
                                </span>
                                <span className="pf-pill pf-pill-blue">
                                    Total Layers {detail.totalLayer}
                                </span>
                                <span className="pf-pill pf-pill-gray">
                                    Last Update {new Date(detail.lastUpdate).toLocaleDateString("en-US")}
                                </span>
                            </div>
                        </div>

                        {/* Tab nav (Display-Board underline style) */}
                        <div className="pf-tabnav">
                            {tabs.map((t, i) => (
                                <button
                                    key={i}
                                    className={`pf-tabnav-item ${activeTab === i ? "active" : ""}`}
                                    onClick={() => setActiveTab(i)}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>

                        <div className="pf-tabcontent">
                            {/* ── Tab 0: Construction & Basics ── */}
                            {activeTab === 0 && (
                                <div className="table-responsive">
                                    <table className="pf-table">
                                        <thead>
                                            <tr>
                                                <th>Lyr No.</th>
                                                <th>Wire Dia (In)</th>
                                                <th>Wire Grade</th>
                                                <th>No.</th>
                                                <th>Wire Space</th>
                                                <th>Dia Loss</th>
                                                <th>Use Carter?</th>
                                                <th>Pitch Angle</th>
                                                <th>Min (In)</th>
                                                <th>Max (In)</th>
                                                <th>Nominal (In)</th>
                                                <th>Layer Tensile</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const editableWindow = resultNo !== 0;
                                                // VB6 always shows a fixed 14-row grid (layers 0-13) on What-If
                                                // windows, with blank editable rows beyond the shaft's current
                                                // layer count - letting a What-If add a new layer just by filling
                                                // one in. The Original only ever shows its real, stored layers.
                                                const blankLayer = (n) => ({
                                                    layerNo: n,
                                                    wireDia: 0,
                                                    wirePerLayer: 0,
                                                    wireGrade: "",
                                                    wireSpace: 0,
                                                    diaLoss: 0,
                                                    useCarter: false,
                                                    layerTensile: 0,
                                                    youngModulus: 0,
                                                    shearModulus: 0,
                                                    spoolType: "",
                                                    machineRPM: 0,
                                                    spoolWeight: 0,
                                                    motorTeeth: 0,
                                                });
                                                const layerRows: ShaftLayer[] = editableWindow
                                                    ? Array.from(
                                                        { length: 14 },
                                                        (_, n) => detail.layers.find((l) => l.layerNo === n) ?? blankLayer(n)
                                                    )
                                                    : detail.layers;

                                                return layerRows.map((layer) => {
                                                    const lc = calc.layerDetails.find(
                                                        (d) => d.layerNo === layer.layerNo
                                                    );
                                                    const editable = resultNo !== 0;
                                                    // A row with no calc data yet, beyond the shaft's known layer
                                                    // count, and with no staged/applied edits either - genuinely
                                                    // untouched, shows blank rather than "0.0000".
                                                    const isBlank =
                                                        !lc &&
                                                        layer.layerNo > (detail.totalLayer ?? 0) &&
                                                        !constructionOverrides[layer.layerNo] &&
                                                        !pendingConstructionEdits[layer.layerNo];
                                                    return (
                                                        <tr key={layer.layerNo}>
                                                            <td className="text-center">
                                                                <span className="pf-layernum">{layer.layerNo}</span>
                                                            </td>
                                                            <td className="p-0">
                                                                {editable ? (
                                                                    <input
                                                                        key={`wireDia-${layer.layerNo}-${lc ? lc.wireDia : layer.wireDia}`}
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        className="pf-inline-input"
                                                                        defaultValue={isBlank ? "" : fmtWireDia(lc ? lc.wireDia : layer.wireDia)}
                                                                        onBlur={(e) => {
                                                                            const v = parseFloat(e.target.value);
                                                                            if (!isNaN(v) && v > 0) stagePendingEdit(layer.layerNo, "wireDia", v);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    lc ? fmtWireDia(lc.wireDia) : fmtWireDia(layer.wireDia)
                                                                )}
                                                            </td>
                                                            <td className="p-0">
                                                                {editable ? (
                                                                    <select
                                                                        key={`wireGrade-${layer.layerNo}-${layer.wireGrade}`}
                                                                        className="pf-inline-input"
                                                                        defaultValue={layer.wireGrade ?? ""}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (v) stagePendingEdit(layer.layerNo, "wireGrade", v);
                                                                        }}
                                                                    >
                                                                        <option value="" disabled={!isBlank}></option>
                                                                        {layer.wireGrade && !wireGrades.includes(layer.wireGrade) && (
                                                                            <option value={layer.wireGrade}>{layer.wireGrade}</option>
                                                                        )}
                                                                        {wireGrades.map((g) => (
                                                                            <option key={g} value={g}>
                                                                                {g}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : (
                                                                    layer.wireGrade
                                                                )}
                                                            </td>
                                                            <td className="p-0">
                                                                {editable ? (
                                                                    <input
                                                                        key={`noOfWires-${layer.layerNo}-${lc ? lc.wirePerLayer : layer.wirePerLayer}`}
                                                                        type="number"
                                                                        step="1"
                                                                        className="pf-inline-input"
                                                                        defaultValue={isBlank ? "" : (lc ? lc.wirePerLayer : layer.wirePerLayer)}
                                                                        onBlur={(e) => {
                                                                            const v = parseInt(e.target.value, 10);
                                                                            if (!isNaN(v) && v > 0) stagePendingEdit(layer.layerNo, "noOfWires", v);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    lc ? lc.wirePerLayer : layer.wirePerLayer
                                                                )}
                                                            </td>
                                                            <td className="p-0">
                                                                {editable ? (
                                                                    <input
                                                                        key={`wireSpace-${layer.layerNo}-${lc ? lc.wireSpace : layer.wireSpace}`}
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        className="pf-inline-input"
                                                                        defaultValue={isBlank ? "" : fmtIn(lc ? lc.wireSpace : layer.wireSpace)}
                                                                        onBlur={(e) => {
                                                                            const v = parseFloat(e.target.value);
                                                                            if (!isNaN(v)) applyImmediateConstructionOverride(layer.layerNo, "wireSpace", v);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    lc ? fmtIn(lc.wireSpace) : fmtIn(layer.wireSpace)
                                                                )}
                                                            </td>
                                                            <td className="p-0">
                                                                {editable ? (
                                                                    <input
                                                                        key={`diaLoss-${layer.layerNo}-${lc ? lc.shrinkage : layer.diaLoss}`}
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        className="pf-inline-input"
                                                                        defaultValue={isBlank ? "" : fmtIn(lc ? lc.shrinkage : layer.diaLoss)}
                                                                        onBlur={(e) => {
                                                                            const v = parseFloat(e.target.value);
                                                                            if (!isNaN(v)) applyImmediateConstructionOverride(layer.layerNo, "diaLoss", v);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    lc ? fmtIn(lc.shrinkage) : fmtIn(layer.diaLoss)
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={lc ? lc.usesCarter : layer.useCarter}
                                                                    readOnly={!editable}
                                                                    onChange={
                                                                        editable
                                                                            ? (e) => applyUseCarterOverride(layer.layerNo, e.target.checked)
                                                                            : undefined
                                                                    }
                                                                />
                                                            </td>
                                                            <td>{!isBlank && lc ? fmt(lc.pitchAngle, 1) : "—"}</td>
                                                            <td>{!isBlank && layer.layerNo > 0 && lc ? fmtIn(lc.layerDiameterMin) : "—"}</td>
                                                            <td>{!isBlank && layer.layerNo > 0 && lc ? fmtIn(lc.layerDiameterMax) : "—"}</td>
                                                            <td className="pf-yellow">
                                                                {isBlank
                                                                    ? ""
                                                                    : lc?.layerDiameter
                                                                        ? fmtIn(lc.layerDiameter)
                                                                        : fmtIn(lc?.wireDia ?? layer.wireDia)}
                                                            </td>
                                                            <td className="pf-yellow">
                                                                {isBlank ? "" : lc ? lc.layerTensile?.toLocaleString() : layer.layerTensile?.toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                            <tr>
                                                <td colSpan={8} className="pf-aht-label">
                                                    After Heat Treater ──────────────────────────→
                                                </td>
                                                <td className="pf-aht">{fmtIn(calc.outerDiameterMin)}</td>
                                                <td className="pf-aht">{fmtIn(calc.outerDiameterMax)}</td>
                                                <td className="pf-aht">{fmtIn(calc.htDiameter)}</td>
                                                <td></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Tab 1: Design Specifications ── */}
                            {activeTab === 1 && (
                                <div className="pf-design-specs-layout">
                                    <table className="pf-table pf-table-narrow">
                                        <thead>
                                            <tr>
                                                <th>Characteristic</th>
                                                <th className="text-center">T.O.L.</th>
                                                <th className="text-center">L.O.L.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                [`Basic Torsional Deflection (${umLbl(3, calc.isMetric)})`, fmt(calc.deflectionTOL, deflectionDecimals(calc.deflectionLOL)), fmt(calc.deflectionLOL, deflectionDecimals(calc.deflectionLOL))],
                                                [`Torsional Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.torsionalStiffnessTOL, torsionalStiffnessDecimals(calc.torsionalStiffnessTOL)), fmt(calc.torsionalStiffnessLOL, torsionalStiffnessDecimals(calc.torsionalStiffnessTOL))],
                                                [`Corrected Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.correctedStiffnessTOL, correctedStiffnessDecimals(calc.rawCombinationStiffnessLOL)), fmt(calc.correctedStiffnessLOL, correctedStiffnessDecimals(calc.rawCombinationStiffnessLOL))],
                                                [`Axial Stretch (${umLbl(8, calc.isMetric)})`, fmt(calc.axialStretchTOL, 5), fmt(calc.axialStretchLOL, 5)],
                                                [`Ultimate Torsional Strength (${umLbl(5, calc.isMetric)})`, fmt(calc.utsTOL, utsDecimals(calc.utsTOL)), fmt(calc.utsLOL, utsDecimals(calc.utsTOL))],
                                                [`Tensile Stress/Unit Torque (${umLbl(6, calc.isMetric)})`, fmtTrim(calc.tensileStressTOL, 3), fmtTrim(calc.tensileStressLOL, 3)],
                                                [`Bending Stress/Unit Curvature (${umLbl(7, calc.isMetric)})`, fmt(calc.bendingStressTOL, 0), fmt(calc.bendingStressLOL, 0)],
                                                [`Diameter (${umLbl(1, calc.isMetric)})`, fmtIn(calc.outerDiameterMax), fmtIn(calc.outerDiameterMin)],
                                                [`Minimum Operating Radius (${umLbl(1, calc.isMetric)})`, fmt(calc.mor, 1), ""],
                                                [`Non-Damaging Radius (${umLbl(1, calc.isMetric)})`, fmt(calc.ndr, 1), ""],
                                                [`Overall Bending Stiffness (${umLbl(4, calc.isMetric)})`, fmt(calc.bendingStiffness, 2), ""],
                                            ].map(([label, tol, lol]) => (
                                                <tr key={label}>
                                                    <td className={label.startsWith("Corrected Stiffness") ? "pf-indent" : undefined}>{label}</td>
                                                    <TolLolCells tol={tol} lol={lol} />
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {(calc.ttrEstimates?.length > 0 || calc.deflectionEstimates?.length > 0) && (
                                        <div className="pf-estimates-panel">
                                            <div className="pf-estimates-title">Estimates on TTR and Deflection</div>
                                            <div className="pf-estimates-warning">
                                                Remember!!! These are only estimates...
                                            </div>

                                            {calc.ttrEstimates?.length > 0 && (
                                                <>
                                                    <div className="pf-estimates-subtitle">TTR</div>
                                                    <table className="pf-table pf-table-estimates">
                                                        <thead>
                                                            <tr>
                                                                <th>Radius ({umLbl(1, calc.isMetric)})</th>
                                                                <th className="text-center">TTR ({umLbl(5, calc.isMetric)})</th>
                                                                {!calc.isMetric && (
                                                                    <th className="text-center">TTR (Oz.In.)</th>
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {calc.ttrEstimates.map((row, i) => (
                                                                <tr key={i}>
                                                                    <td className="pf-yellow text-center">{fmtTrim(row.radius, 1)}</td>
                                                                    <td className="pf-yellow text-center">{fmt(row.ttrLbIn, 2)}</td>
                                                                    {!calc.isMetric && (
                                                                        <td className="pf-yellow text-center">{fmt(row.ttrOzIn, 1)}</td>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                            <tr>
                                                                <td className="pf-white-input p-0">
                                                                    <input
                                                                        type="number"
                                                                        className="pf-inline-input"
                                                                        placeholder="Type radius…"
                                                                        value={customRadius}
                                                                        onChange={(e) => setCustomRadius(e.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="pf-yellow text-center">
                                                                    {customTtrLbIn != null ? fmt(customTtrLbIn, 2) : ""}
                                                                </td>
                                                                {!calc.isMetric && (
                                                                    <td className="pf-yellow text-center">
                                                                        {customTtrOzIn != null ? fmt(customTtrOzIn, 1) : ""}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                    <div className="pf-estimates-formula">
                                                        Formula Used: .2 × Bending Stiffness/Radius. It was then
                                                        multiplied by a correction factor of 3.3
                                                    </div>
                                                </>
                                            )}

                                            {calc.deflectionEstimates?.length > 0 && (
                                                <>
                                                    <div className="pf-estimates-subtitle mt-3">Deflection</div>
                                                    <table className="pf-table pf-table-estimates">
                                                        <thead>
                                                            <tr>
                                                                <th>Torque ({umLbl(5, calc.isMetric)})</th>
                                                                <th className="text-center">TOL ({umLbl(11, calc.isMetric)})</th>
                                                                <th className="text-center">LOL ({umLbl(11, calc.isMetric)})</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {calc.deflectionEstimates.map((row, i) => (
                                                                <tr key={i}>
                                                                    <td className="pf-yellow text-center">{fmt(row.torque, 2)}</td>
                                                                    <td className="pf-yellow text-center">{fmt(row.defTOL, 1)}</td>
                                                                    <td className="pf-yellow text-center">{fmt(row.defLOL, 1)}</td>
                                                                </tr>
                                                            ))}
                                                            <tr>
                                                                <td className="pf-white-input p-0">
                                                                    <input
                                                                        type="number"
                                                                        className="pf-inline-input"
                                                                        placeholder="Type torque…"
                                                                        value={customTorque}
                                                                        onChange={(e) => setCustomTorque(e.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="pf-yellow text-center">
                                                                    {customDefTOL != null ? fmt(customDefTOL, 1) : ""}
                                                                </td>
                                                                <td className="pf-yellow text-center">
                                                                    {customDefLOL != null ? fmt(customDefLOL, 1) : ""}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                    <div className="pf-estimates-formula">
                                                        Formula Used: Adding 20% factor of safety to calculated Basic
                                                        Torsional Deflection AND adding intercept value of 20% of
                                                        deflection at 1 Lb.In.
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Tab 2: Axial Stretch ── */}
                            {activeTab === 2 && (
                                <>
                                    <div className="table-responsive">
                                        <table className="pf-table pf-axial-table">
                                            <thead>
                                                <tr>
                                                    <th rowSpan={2}>Layer</th>
                                                    <th colSpan={2} className="text-center">Overall</th>
                                                    <th colSpan={2} className="text-center">Pair</th>
                                                    <th colSpan={2} className="text-center">Combination</th>
                                                </tr>
                                                <tr>
                                                    <th className="text-center">T.O.L.</th>
                                                    <th className="text-center">L.O.L.</th>
                                                    <th className="text-center">T.O.L.</th>
                                                    <th className="text-center">L.O.L.</th>
                                                    <th className="text-center">T.O.L.</th>
                                                    <th className="text-center">L.O.L.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calc.axialStretchLayers.map((row) => (
                                                    <tr key={row.layerNo}>
                                                        <td className="text-center">{row.layerNo}</td>
                                                        <td className={`text-center ${calc.preferredTOL === 1 ? "pf-yellow" : ""}`}>{fmt(row.overallTOL, 5)}</td>
                                                        <td className={`text-center ${calc.preferredLOL === 1 ? "pf-yellow" : ""}`}>{fmt(row.overallLOL, 5)}</td>
                                                        <td className={`text-center ${calc.preferredTOL === 2 ? "pf-yellow" : ""}`}>{fmt(row.pairTOL, 5)}</td>
                                                        <td className={`text-center ${calc.preferredLOL === 2 ? "pf-yellow" : ""}`}>{fmt(row.pairLOL, 5)}</td>
                                                        <td className={`text-center ${calc.preferredTOL === 3 ? "pf-yellow" : ""}`}>{fmt(row.combinationTOL, 5)}</td>
                                                        <td className={`text-center ${calc.preferredLOL === 3 ? "pf-yellow" : ""}`}>{fmt(row.combinationLOL, 5)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="table-responsive mt-3">
                                        <table className="pf-table pf-axial-table" style={{ maxWidth: 640 }}>
                                            <tbody>
                                                {/* VB6 (ShowResults, lines 19610-19621): txtAxStretch(K)/txtAxStiff(K)
                                                    BackStyle/BorderStyle are set to 1 (highlighted) for the preferred
                                                    TOL index, but the very next statement is "For I = 1 To 6 ...
                                                    BackStyle = 0 ... Next I", which unconditionally resets ALL SIX
                                                    cells' highlighting - including the one just set. Net effect: this
                                                    summary row never shows any highlighting in real VB6, regardless
                                                    of preferredTOL/preferredLOL. Confirmed by reading the source
                                                    directly - not a design choice to second-guess. This is separate
                                                    from the per-layer table above (lines 19582-19589), which DOES
                                                    correctly and permanently highlight one TOL column and one LOL
                                                    column per layer - that logic is unaffected by this fix. */}
                                                <tr>
                                                    <td>Axial Stretch: ({umLbl(4, calc.isMetric)})</td>
                                                    <td className="text-center">{fmt(calc.overallAxialStretchTOL, 5)}</td>
                                                    <td className="text-center">{fmt(calc.overallAxialStretchLOL, 5)}</td>
                                                    <td className="text-center">{fmt(calc.pairAxialStretchTOL, 5)}</td>
                                                    <td className="text-center">{fmt(calc.pairAxialStretchLOL, 5)}</td>
                                                    <td className="text-center">{fmt(calc.combinationAxialStretchTOL, 5)}</td>
                                                    <td className="text-center">{fmt(calc.combinationAxialStretchLOL, 5)}</td>
                                                </tr>
                                                <tr>
                                                    <td>Stiffness: ({umLbl(8, calc.isMetric)})</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawOverallStiffnessTOL, calc.overallStiffnessTOL)}</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawOverallStiffnessLOL, calc.overallStiffnessLOL)}</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawPairStiffnessTOL, calc.pairStiffnessTOL)}</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawPairStiffnessLOL, calc.pairStiffnessLOL)}</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawCombinationStiffnessTOL, calc.combinationStiffnessTOL)}</td>
                                                    <td className="text-center">{axStiffCellDisplay(calc.rawCombinationStiffnessLOL, calc.combinationStiffnessLOL)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* ── Tab 3: Time Estimates & Cost ── */}
                            {activeTab === 3 && (
                                <>
                                    <div className="pf-estimates-title mb-2">
                                        Winding Standard For Auto &amp; Carter (Only applicable for Conventional winding)
                                    </div>
                                    <div className="table-responsive">
                                        <table className="pf-table pf-winding-table">
                                            <thead>
                                                <tr>
                                                    <th>Lyr No.</th>
                                                    <th>Spool Type</th>
                                                    <th>Mach. RPM</th>
                                                    <th>Spool Lbs.</th>
                                                    <th>Work Min.</th>
                                                    <th>Run Min.</th>
                                                    <th>Feet/Set</th>
                                                    <th>Feet/Shift</th>
                                                    <th>Work Min/Hr.</th>
                                                    <th>Min./Feet</th>
                                                    <th>UH Per 1000</th>
                                                    <th>No. of Spools</th>
                                                    <th>Mach. Min.</th>
                                                    <th>Labor Min.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calc.windingStandardLayers.map((row) => {
                                                    const isCarterRow = row.machineRPM == null;
                                                    return (
                                                        <tr key={row.layerNo}>
                                                            <td className="text-center">
                                                                <span className="pf-layernum">{row.layerNo}</span>
                                                            </td>
                                                            <td className="pf-yellow p-0">
                                                                {isCarterRow ? (
                                                                    <div className="pf-inline-input text-center">{row.spoolType}</div>
                                                                ) : (
                                                                    <select
                                                                        className="pf-inline-input"
                                                                        value={row.spoolType}
                                                                        onChange={(e) =>
                                                                            applyWindingOverride(row.layerNo, "spoolType", e.target.value)
                                                                        }
                                                                    >
                                                                        <option value="Wide">Wide</option>
                                                                        <option value="Narrow">Narrow</option>
                                                                    </select>
                                                                )}
                                                            </td>
                                                            <td className="pf-yellow p-0">
                                                                {!isCarterRow && (
                                                                    <input
                                                                        type="number"
                                                                        className="pf-inline-input"
                                                                        defaultValue={row.machineRPM}
                                                                        onBlur={(e) => {
                                                                            const v = parseFloat(e.target.value);
                                                                            if (v > 0) applyWindingOverride(row.layerNo, "machineRPM", v);
                                                                        }}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="pf-yellow p-0">
                                                                {!isCarterRow && (
                                                                    <input
                                                                        type="number"
                                                                        className="pf-inline-input"
                                                                        defaultValue={row.spoolWeight}
                                                                        onBlur={(e) => {
                                                                            const v = parseFloat(e.target.value);
                                                                            if (v > 0) applyWindingOverride(row.layerNo, "spoolWeight", v);
                                                                        }}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="pf-yellow text-center">{fmt(row.workMin, 0)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.runMin, 0)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.feetPerSet, 0)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.feetPerShift, 0)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.workMinPerHr, 1)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.minPerFeet, 4)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.unitPer1000, 1)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.noOfSpools, 1)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.machMin, 0)}</td>
                                                            <td className="pf-yellow text-center">{fmt(row.laborMin, 0)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="pf-heat-treater-caption mt-2 mb-3">
                                        Heat Treater - {fmt(calc.heatTreaterFeetPerShift, 0)} Feet Per Shift running at{" "}
                                        {fmt(calc.heatTreaterSpeedFtPerMin, 0)} Feet/Min and 60% utilization
                                    </div>

                                    <div className="pf-time-cost-panels">
                                        <div className="pf-time-cost-panel">
                                            <div className="pf-estimates-subtitle">Cost Per 100 Feet</div>
                                            <table className="pf-table pf-table-estimates">
                                                <thead>
                                                    <tr>
                                                        <th>Material</th>
                                                        <th>Labor</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="pf-yellow text-center">${fmt(calc.materialCostPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">${fmt(calc.laborCostPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">${fmt(calc.totalCostPer100Ft, 1)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="pf-time-cost-panel">
                                            <div className="pf-estimates-subtitle">Labor Minutes per 100 Feet</div>
                                            <table className="pf-table pf-table-estimates">
                                                <thead>
                                                    <tr>
                                                        <th>Auto Wind</th>
                                                        <th>Auto Spool</th>
                                                        <th>Carter Wind+Spl</th>
                                                        <th>Heat Treater</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="pf-yellow text-center">{fmt(calc.laborAutoWindPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.laborAutoSpoolPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.laborCarterPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.laborHeatTreaterPer100Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.laborMinAllPer100Ft, 1)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="pf-time-cost-panel">
                                            <div className="pf-estimates-subtitle">Machine Hours per 1000 Feet</div>
                                            <table className="pf-table pf-table-estimates">
                                                <thead>
                                                    <tr>
                                                        <th>Spooling</th>
                                                        <th>Winding</th>
                                                        <th>Carter Wind</th>
                                                        <th>Heat Treater</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="pf-yellow text-center">{fmt(calc.machTimeSpoolingPer1000Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.machTimeWindingPer1000Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.machTimeCarterPer1000Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.machTimeHTPer1000Ft, 1)}</td>
                                                        <td className="pf-yellow text-center">{fmt(calc.machineTimePer1000Ft, 1)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            <div className="pf-wire-req mt-2">
                                                Wire Requirment: {fmt(calc.wireReqPer100Ft, 1)} Lbs. Per 100 Feet
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ── Tab 4: ESM Settings ── */}
                            {activeTab === 4 && (
                                <>
                                    <div className="pf-esm-header">
                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">Select Machine:</span>
                                            <label className="pf-radio">
                                                <input
                                                    type="radio"
                                                    checked={selectMachine === "ESM"}
                                                    onChange={() => setSelectMachine("ESM")}
                                                />{" "}
                                                ESM
                                            </label>
                                            <label className="pf-radio">
                                                <input
                                                    type="radio"
                                                    checked={selectMachine === "MSM"}
                                                    onChange={() => setSelectMachine("MSM")}
                                                />{" "}
                                                MSM
                                            </label>
                                        </div>

                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">Linear Speed (Ft Per Min.):</span>
                                            <input
                                                type="number"
                                                className="pf-white-input pf-esm-input"
                                                defaultValue={calc.esmLinearSpeedUsed}
                                                onBlur={(e) => {
                                                    const v = parseFloat(e.target.value);
                                                    if (v > 0) applyEsmOverride("linearSpeed", v);
                                                }}
                                            />
                                        </div>

                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">ESM Machine:</span>
                                            <label className="pf-radio">
                                                <input
                                                    type="radio"
                                                    checked={calc.esmIsFourHead}
                                                    onChange={() => applyEsmOverride("isFourHead", true)}
                                                />{" "}
                                                Four Head
                                            </label>
                                            <label className="pf-radio">
                                                <input
                                                    type="radio"
                                                    checked={!calc.esmIsFourHead}
                                                    onChange={() => applyEsmOverride("isFourHead", false)}
                                                />{" "}
                                                Five Head
                                            </label>
                                        </div>

                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">Starting Layer No.:</span>
                                            <input
                                                type="number"
                                                className="pf-white-input pf-esm-input"
                                                defaultValue={calc.esmLayers[0]?.layerNo}
                                                onBlur={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    if (v > 0) applyEsmOverride("startLayer", v);
                                                }}
                                            />
                                        </div>
                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">Final Layer No.:</span>
                                            <input
                                                type="number"
                                                className="pf-white-input pf-esm-input"
                                                defaultValue={calc.esmLayers[calc.esmLayers.length - 1]?.layerNo}
                                                onBlur={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    if (v > 0) applyEsmOverride("endLayer", v);
                                                }}
                                            />
                                        </div>
                                        <div className="pf-esm-field">
                                            <span className="pf-esm-label">Head No. To Use for starting Layer No.:</span>
                                            <input
                                                type="number"
                                                className="pf-white-input pf-esm-input"
                                                defaultValue={calc.esmLayers[0]?.headNo}
                                                onBlur={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    if (v > 0) applyEsmOverride("firstHead", v);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {calc.esmMismatch && (
                                        <div className="pf-wire-req mt-2 mb-2">
                                            Mismatch
                                            the head count doesn't cover the requested layer range.
                                        </div>
                                    )}

                                    <div className="pf-esm-capstan-row mt-3 mb-2">
                                        <span>Capstan Dia.: <strong>{fmt(calc.esmCapstanDia, 2)}</strong></span>
                                        <span>GearDn: <strong>{fmt(calc.esmCapstanGearDown, 0)}</strong></span>
                                        <span>Capstan RPM: <strong>{fmt(calc.esmCapstanRPM, 1)}</strong></span>
                                        <span>Capstan Motor RPM: <strong>{fmt(calc.esmCapstanMotorRPM, 0)}</strong></span>
                                        <span>Capstan Freq.: <strong>{fmt(calc.esmCapstanFreq, 2)}</strong></span>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="pf-table pf-esm-table">
                                            <thead>
                                                <tr>
                                                    <th>Head No.</th>
                                                    <th>Layer No.</th>
                                                    <th>Wires/Layer</th>
                                                    <th>Wire Space</th>
                                                    <th>Cage RPM</th>
                                                    <th>Cage Teeth</th>
                                                    <th>Motor Teeth</th>
                                                    <th>Motor RPM</th>
                                                    <th>Motor Freq. Program</th>
                                                    <th>Motor Freq. Run</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calc.esmLayers.map((row) => (
                                                    <tr key={row.layerNo}>
                                                        <td className="pf-yellow text-center">{row.headNo}</td>
                                                        <td className="pf-yellow text-center">{row.layerNo}</td>
                                                        <td className="pf-yellow text-center">{row.wiresPerLayer}</td>
                                                        <td className="pf-yellow text-center">{fmt(row.wireSpace, 4)}</td>
                                                        <td className="pf-yellow text-center">{fmt(row.cageRPM, 0)}</td>
                                                        {/* VB6: MSM_Click only calls ShowResults, never EsmCal —
                                                            these motor-related columns render blank under MSM
                                                            rather than showing stale/leftover ESM values. */}
                                                        <td className="pf-yellow text-center">
                                                            {selectMachine === "ESM" ? row.cageTeeth : ""}
                                                        </td>
                                                        <td className="pf-white-input p-0">
                                                            {selectMachine === "ESM" ? (
                                                                <input
                                                                    key={`mt-${row.layerNo}-${row.headNo}-${row.motorTeeth}-${motorTeethResetKey}`}
                                                                    type="number"
                                                                    className="pf-inline-input"
                                                                    defaultValue={row.motorTeeth}
                                                                    onBlur={(e) => {
                                                                        const v = parseInt(e.target.value, 10);
                                                                        if (v > 0) applyEsmLayerOverride(row.layerNo, v);
                                                                    }}
                                                                />
                                                            ) : (
                                                                ""
                                                            )}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {selectMachine === "ESM" ? fmt(row.motorRPM, 0) : ""}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {selectMachine === "ESM" ? fmt(row.motorFreqProgram, 1) : ""}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {selectMachine === "ESM" ? fmt(row.motorFreqRun, 1) : ""}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {selectMachine === "ESM" && Object.keys(esmLayerOverrides).length > 0 && (
                                        <button
                                            type="button"
                                            className="pf-restore-btn mt-2"
                                            onClick={() => {
                                                setEsmLayerOverrides({});
                                                setMotorTeethResetKey((k) => k + 1);
                                                sendOverrides(windingOverrides, esmOverride, {});
                                            }}
                                        >
                                            Restore Original MotorTeeth
                                        </button>
                                    )}
                                </>
                            )}

                            {/* ── Tab 5: Spooling Requirement ── */}
                            {activeTab === 5 && (
                                <div className="pf-spooling-layout">
                                    <div className="pf-spooling-side">
                                        <div className="pf-spooling-box text-center">
                                            <div className="pf-esm-label mb-1">Feet To Be Wound</div>
                                            <input
                                                type="number"
                                                className="pf-white-input pf-esm-input"
                                                style={{ width: "100%", textAlign: "center" }}
                                                defaultValue={calc.spoolingFootageToBeWound}
                                                onBlur={(e) => {
                                                    const v = parseFloat(e.target.value);
                                                    if (v > 0) applySpoolingOverride("footageToBeWound", v);
                                                }}
                                            />
                                        </div>

                                        <div className="pf-spooling-box">
                                            <div className="pf-esm-label mb-1">Spooler</div>
                                            {["Conventional", "ESM", "MSM"].map((opt) => (
                                                <label className="pf-radio d-block" key={opt}>
                                                    <input
                                                        type="radio"
                                                        checked={calc.spoolingSpoolerType === opt}
                                                        onChange={() => applySpoolingOverride("spoolerType", opt)}
                                                    />{" "}
                                                    {opt}
                                                </label>
                                            ))}
                                        </div>

                                        <div className="pf-spooling-summary-box">
                                            <div className="pf-spooling-summary-item">
                                                <div className="pf-spooling-summary-label">Spools/Order</div>
                                                <div className="pf-spooling-summary-value">
                                                    {fmt(calc.spoolingTotalSpoolsPerOrder, 0)}
                                                </div>
                                            </div>
                                            <div className="pf-spooling-summary-item">
                                                <div className="pf-spooling-summary-label">Lbs. of Wire/Order</div>
                                                <div className="pf-spooling-summary-value">
                                                    {fmt(calc.spoolingTotalWirePerOrder, 0)}
                                                </div>
                                            </div>
                                            <div className="pf-spooling-summary-item">
                                                <div className="pf-spooling-summary-label">Overall Scrap %</div>
                                                <div className="pf-spooling-summary-value">
                                                    {fmt(calc.spoolingOverallScrapPercent, 1)} %
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-responsive pf-spooling-table-wrap">
                                        <table className="pf-table pf-spooling-table">
                                            <thead>
                                                <tr>
                                                    <th>Lyr No.</th>
                                                    <th>Wire Dia.</th>
                                                    <th>Type</th>
                                                    <th>Turns/Spool</th>
                                                    <th>Lbs./Spool</th>
                                                    <th>Spls/Order</th>
                                                    <th>Spool Type</th>
                                                    <th>Wire Req. In Lbs.</th>
                                                    <th>Layer Feet</th>
                                                    <th>Spooling Mach.</th>
                                                    <th>Time Labor</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calc.spoolingLayers.map((row) => (
                                                    <tr key={row.layerNo}>
                                                        <td className="text-center">
                                                            <span className="pf-layernum">{row.layerNo}</span>
                                                        </td>
                                                        <td className="pf-yellow text-center">{fmtIn(row.wireDia)}</td>
                                                        <td className="pf-yellow text-center">{row.wireGrade}</td>
                                                        <td className="pf-yellow text-center">
                                                            {row.turnsPerSpool != null ? fmt(row.turnsPerSpool, 0) : ""}
                                                            {row.psTurnsPerSpool != null && (
                                                                <div style={{ fontSize: "0.75em", fontStyle: "italic", color: "var(--ssw-fg-2, #666)" }}>
                                                                    Partial Set<br />{fmt(row.psTurnsPerSpool, 0)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {row.lbsPerSpool != null ? fmt(row.lbsPerSpool, 1) : ""}
                                                            {row.psLbsPerSpool != null && (
                                                                <div style={{ fontSize: "0.75em", fontStyle: "italic", color: "var(--ssw-fg-2, #666)" }}>
                                                                    Partial Set<br />{fmt(row.psLbsPerSpool, 1)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {row.spoolsPerOrder != null ? fmt(row.spoolsPerOrder, 0) : ""}
                                                            {row.psSpoolsPerOrder != null && (
                                                                <div style={{ fontSize: "0.75em", fontStyle: "italic", color: "var(--ssw-fg-2, #666)" }}>
                                                                    Partial Set<br />{fmt(row.psSpoolsPerOrder, 0)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="pf-yellow text-center">{row.spoolType ?? ""}</td>
                                                        <td className="pf-yellow text-center">{fmt(row.wireReqLbs, 0)}</td>
                                                        <td className="pf-yellow text-center">
                                                            {row.layerFeet != null ? fmt(row.layerFeet, 0) : ""}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {row.machTime != null ? fmt(row.machTime, 0) : ""}
                                                        </td>
                                                        <td className="pf-yellow text-center">
                                                            {row.laborTime != null ? fmt(row.laborTime, 0) : ""}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ─── What-If floating window ────────────────────────────────────────────────────
// A real floating, draggable window rather than a blocking modal - matching VB6's
// genuinely separate windows (cascaded on open via "Me.Left = (TotalWhatIf-1)*300"),
// which the user can freely move to see the Original underneath. Everything still
// stays in the same React tree (no actual new browser window) so ReCalculate can
// write straight into the shared Compare Results state below.
const WhatIfModal = ({
    resultNo,
    children,
    onClose,
}) => {
    const windowRef = useRef(null);
    // Null until measured on mount - avoids a flash at the wrong position before centering.
    const [pos, setPos] = useState(null);
    const dragRef = useRef(null);

    // Center on the actual rendered size on first mount, with a small cascade per
    // resultNo (VB6 cascades new windows too, via "Me.Left = (TotalWhatIf-1)*300",
    // just from a corner rather than the center).
    useEffect(() => {
        if (!windowRef.current) return;
        const rect = windowRef.current.getBoundingClientRect();
        const cascade = (resultNo - 1) * 32;
        setPos({
            x: Math.max(0, (window.innerWidth - rect.width) / 2 + cascade),
            y: Math.max(0, (window.innerHeight - rect.height) / 2 + cascade),
        });
    }, []);

    const onTitleBarMouseDown = (e) => {
        if (!pos) return;
        dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
        const onMouseMove = (ev) => {
            if (!dragRef.current) return;
            const dx = ev.clientX - dragRef.current.startX;
            const dy = ev.clientY - dragRef.current.startY;
            setPos({ x: Math.max(0, dragRef.current.origX + dx), y: Math.max(0, dragRef.current.origY + dy) });
        };
        const onMouseUp = () => {
            dragRef.current = null;
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    };

    return (
        <div
            ref={windowRef}
            className="pf-whatif-window"
            style={pos ? { left: pos.x, top: pos.y, visibility: "visible" } : { visibility: "hidden" }}
        >
            <div className="pf-whatif-titlebar" onMouseDown={onTitleBarMouseDown}>
                <span>What-If {resultNo}</span>
                <button
                    type="button"
                    className="pf-whatif-close"
                    onClick={onClose}
                    aria-label={`Close What-If ${resultNo}`}
                >
                    ✕
                </button>
            </div>
            <div className="pf-whatif-body">{children}</div>
        </div>
    );
};

// ─── Shell: shaft selection, the Original editor, and What-If windows ──────────────
export default function PerflexionApp() {
    const [shafts, setShafts] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedName, setSelectedName] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    // VB6 mdiShaftDesign.frm WhatIf_Click: TotalWhatIf starts at 1, only ever
    // increments (never reused even after a What-If window is closed), and the
    // button hides once a 4th What-If has been opened ("If NextWINo + 1 = 5").
    const [nextWhatIfNo, setNextWhatIfNo] = useState(1);
    const [openWhatIfs, setOpenWhatIfs] = useState([]);

    // Shared Compare Results data, keyed by resultNo (0 = Original, 1-4 = What-If) -
    // same role as VB6's myResults, populated by whichever editor last recalculated.
    const [compareResults, setCompareResults] = useState({});

    const handleCalcChange = (resultNo, calc) => {
        setCompareResults((prev) => ({ ...prev, [resultNo]: calc }));
    };

    useEffect(() => {
        fetch(`${API_BASE}/api/shafts`)
            .then((r) => r.json())
            .then(setShafts)
            .catch(console.error);
    }, []);

    // A new shaft selection resets the whole session - matching VB6's NewShaft(),
    // which resets TotalWhatIf to 1 and closes every open window for the prior shaft.
    const handleSelectShaft = (name) => {
        setSelectedName(name);
        setSearch(name);
        setShowDropdown(false);
        setNextWhatIfNo(1);
        setOpenWhatIfs([]);
        setCompareResults({});
    };

    const handleOpenWhatIf = () => {
        const n = nextWhatIfNo;
        setNextWhatIfNo(n + 1);
        setOpenWhatIfs((prev) => [...prev, n]);
    };

    // Closing a What-If only removes its window - VB6 never clears a closed window's
    // column from myResults, so its last-computed values stay in Compare Results.
    const handleCloseWhatIf = (n) => {
        setOpenWhatIfs((prev) => prev.filter((x) => x !== n));
    };

    const filtered = shafts.filter((s) =>
        s.shaftName.toLowerCase().includes(search.toLowerCase())
    );

    const whatIfButtonVisible = selectedName != null && nextWhatIfNo <= 4;

    return (
        <div className="perflexion-app">
            {/* ── Banner (Display-Board style) ── */}
            <div className="pf-banner">
                <div className="pf-banner-text">
                    <span className="pf-eyebrow">Design Tools</span>
                    <div className="pf-logo-chip">
                        <img src={perflexionLogo} alt="Perflexion — Ultimate Flexible Shaft Design Software" className="pf-logo-img" />
                    </div>
                </div>

                <div className="pf-banner-search">
                    <label className="pf-search-label">Select Shaft</label>
                    <div className="position-relative">
                        <input
                            className="pf-search-input"
                            placeholder="Type to search…"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setSelectedName(null);
                                setShowDropdown(true);
                            }}
                            onFocus={() => setShowDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                        />
                        {showDropdown && search.length > 0 && filtered.length > 0 && (
                            <div className="pf-dropdown">
                                {filtered.map((sh) => (
                                    <div
                                        key={sh.shaftId}
                                        className="pf-dropdown-item"
                                        onMouseDown={() => handleSelectShaft(sh.shaftName)}
                                    >
                                        {sh.shaftName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {whatIfButtonVisible && (
                    <button type="button" className="pf-whatif-open-btn" onClick={handleOpenWhatIf}>
                        Open New What If
                    </button>
                )}
            </div>

            {!selectedName && (
                <div className="pf-card pf-placeholder">
                    Type a shaft name above to load its design data.
                </div>
            )}

            {selectedName && (
                <>
                    <ShaftEditor
                        key={selectedName}
                        shaftName={selectedName}
                        resultNo={0}
                        onCalcChange={handleCalcChange}
                    />

                    {openWhatIfs.map((n) => (
                        <WhatIfModal key={n} resultNo={n} onClose={() => handleCloseWhatIf(n)}>
                            <ShaftEditor
                                shaftName={selectedName}
                                resultNo={n}
                                onCalcChange={handleCalcChange}
                            />
                        </WhatIfModal>
                    ))}

                    <CompareResultsPanel results={compareResults} />
                </>
            )}
        </div>
    );
}
